import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { TodaySummaryResponse } from '../api/contracts';
import { useNotifications } from '../notifications/useNotifications';

export const TODAY_SUMMARY_QUERY_KEY = ['summary', 'today'] as const;

/**
 * GET /api/summary/today — the Overview page's primary data source.
 *
 * `totals` on the response is already computed server-side as
 * `mains + solarBatteryServed` (see docs/PLAN.md's energy model section) —
 * callers must treat it as authoritative and never recompute or cross-check
 * it by summing channels/series client-side.
 *
 * Refetches every minute since this backs the page the user checks most and
 * a new derived-metrics point lands roughly that often.
 *
 * Query-level `onError` was removed in TanStack Query v5, so errors are
 * routed to the shared NotificationProvider (the same toast path every other
 * API error in this app uses) via this effect instead of a second,
 * page-local error UI.
 */
export function useTodaySummary() {
  const { notifyError } = useNotifications();
  const query = useQuery<TodaySummaryResponse>({
    queryKey: TODAY_SUMMARY_QUERY_KEY,
    queryFn: () => apiClient.get<TodaySummaryResponse>('/api/summary/today'),
    refetchInterval: 60_000,
  });

  const notifiedError = useRef<unknown>(undefined);
  useEffect(() => {
    if (query.error && query.error !== notifiedError.current) {
      notifiedError.current = query.error;
      notifyError(query.error, "load today's energy summary");
    }
  }, [query.error, notifyError]);

  return query;
}
